/**
 * @ngdoc class
 * @name Utils
 *
 * @description
 * The main purpose of this class is to provide programmer with basic functions, which are frequently used and aren't
 * present in the standard. Knowing that almost all of traditional utility libraries like underscore can be superseded
 * by ES6 functions, there's a will to get rid off them.
 *
 */
/* exported Utils */
/*eslint no-unused-vars: "off"*/
class Utils {

    /**
     * @description
     * Creates a deep copy of an object comprising of only those attributes that are needed by a caller.
     *
     * @param object Object from which properties will be taken.
     * @param attributes The possible mix of string sequences and string arrays used to pick up all demanded attributes.
     */
    static subset(object, ...attributes) {
        // Makes deep copy.
        const copy = angular.copy(object);

        // User may pass arrays of attributes as well, which should be flattened to one long list of attributes.
        const flattened = Utils.flatten(attributes);

        // Takes only subset of attributes.
        return flattened.reduce((output, attribute) => {
            output[attribute] = copy[attribute];
            return output;
        }, {});
    }

    /**
     * @description
     * Creates a full immutable object from the given object parameter.
     *
     * @param object Object which immutable copy will be returned.
     */
    static makeImmutable(object) {
        if (object instanceof Object && !(object instanceof Function) && !Object.isFrozen(object)) {
            Object.freeze(object);

            Object.getOwnPropertyNames(object).forEach((property) => {
                if (object.hasOwnProperty(property)) {
                    Utils.makeImmutable(object[property]);
                }
            });
        }

        return object;
    }

    /**
     * @description
     * Gets writable subset view of the given object. All fields changed in a view are reflected in an original object.
     *
     * @param object Original object that may reflect changes.
     * @param properties Subset of properties that we are interesting at looking at and changing.
     */
    static writableSubsetView(object, ...properties) {
        // User may pass arrays of attributes as well, which should be flattened to one long list of attributes.
        let flattened = Utils.flatten(properties);

        // Take all properties if none of them were declared.
        flattened = flattened.length === 0 ? Object.getOwnPropertyNames(object) : flattened;

        const view = {};
        flattened.forEach((property) => {
            // Changed to primitives are mirrored to the original object.
            // Changes to object that are deeper in the hierarchy are handled by default.
            if (object.hasOwnProperty(property)) {
                Object.defineProperty(view, property, {
                    enumerable: true,
                    set: value => object[property] = value,
                    get: () => object[property]
                });
            }
        });

        return view;
    }

    /**
     * @description
     * Gets readonly view of the given object. View cannot be changed accessed and it shows current state of the
     * original object.
     *
     * @param object Original object that will be changed over time.
     * @param attributes Subset of properties that we are interesting at looking at.
     */
    static readonlySubsetView(target, object, ...attributes) {
        // User may pass arrays of attributes as well, which should be flattened to one long list of attributes.
        const flattened = Utils.flatten(attributes);

        const view = target || {};
        makeReadonly(view, object, flattened.length === 0 ? undefined : flattened);

        // Recursively add getters to every object in hierarchy.
        function makeReadonly(view, object, propertySubset) {
            const properties = propertySubset || Object.getOwnPropertyNames(object);

            properties.forEach((property) => {
                if (object.hasOwnProperty(property)) {
                    if (object[property] instanceof Object && !(object[property] instanceof Function)) {
                        // In case of object, create new readonly object in view and repeat whole operation for it.
                        Object.defineProperty(view, property, {
                            value: {}, writable: false, enumerable: true
                        });

                        makeReadonly(view[property], object[property]);
                    } else {
                        // In case property is not a {} object just define property as a getter referencing observed value.
                        Object.defineProperty(view, property, {
                            get: () => object[property], enumerable: true,
                        });
                    }
                }
            });
        }

        return view;
    }

    /**
     * @description
     * This is an assign function that copies full descriptors.
     *
     * @param target Object to which all sources will be assigned (merged).
     * @param sources All objects from which all properties will be copied.
     */
    static completeAssign(target, ...sources) {
        sources.forEach((source) => {
            const descriptors = Object.keys(source).reduce((descriptors, key) => {
                descriptors[key] = Object.getOwnPropertyDescriptor(source, key);
                return descriptors;
            }, {});

            // By default, Object.assign copies enumerable symbols. Let's copy all of them.
            Object.getOwnPropertySymbols(source).forEach((symbol) => {
                descriptors[symbol] = Object.getOwnPropertyDescriptor(source, symbol);
            });

            Object.defineProperties(target, descriptors);
        });

        return target;
    }

    /**
     * @description
     * Flattens a nested array (the nesting can be to any depth).
     *
     * @param array Array of arrays to be flattened.
     */
    static flatten(array) {
        while (array.find(element => Array.isArray(element))) {
            array = Array.concat(...array);
        }
        return array;
    }

     /**
     * @description
     * Check if the object passed is an empty object
     *
     * @param obj Object to be checked
     */
    static isEmptyObject(obj) {
        return Object.keys(obj).length === 0;
    }

}

export default Utils;



// WEBPACK FOOTER //
// ../node_modules/bookingbug-angular/src/core/_functions/utils.js